// WHERE FOCUS GOES WHEN A DIALOG CLOSES (QA 2026-09-30, C1-11; `docs/decisions.md`
// D-240).
//
// Fluent restores focus through tabster, and tabster returns it only to an
// element that was focused while carrying a restore-target attribute, which
// `DialogTrigger` or `useRestoreFocusTarget` would add. No dialog here opens
// from a `DialogTrigger`, so nothing was ever restored: closing any dialog left
// focus on `document.body`, and an Escape after cancelling an "Are you sure?"
// never reached the invoice modal behind it.
//
// `AppDialog` records the element that had focus when the dialog opened and,
// when it closes, asks `restoreTarget` whether to put focus back there. Pure,
// so the suite can check the rule without a renderer.

/** What `restoreTarget` needs from an element: whether it is still in the
    page, and a way to focus it. */
export interface FocusableLike {
  readonly isConnected: boolean
  focus(options?: { preventScroll?: boolean }): void
}

/**
 * The element to focus as a dialog closes, or `null` to leave focus alone.
 *
 * - The opener must still be in the page. A control that was removed (the
 *   queue card of an invoice that left the queue) cannot take focus.
 * - Focus must have been lost: `active` is the document's body, or nothing.
 *   If the closing action already put focus somewhere on purpose, leave it.
 * - The opener must not be the body itself: a dialog opened with nothing
 *   focused has nothing to return to.
 */
export function restoreTarget<T extends FocusableLike>(
  opener: T | null | undefined,
  active: unknown,
  body: unknown,
): T | null {
  if (!opener || (opener as unknown) === body) return null
  if (!opener.isConnected) return null
  if (active !== null && active !== undefined && active !== body) return null
  return opener
}
